import * as MuiIcons from "@mui/icons-material";
import {useContext, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useAmexReportsRepository } from "../../../api/repositories/AmexReportsRepository";
import { useRecoilState } from "recoil";
import { STATE_TABLE_UPDATING } from "../constants/ReportRecoil";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import * as Yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {
    Box, Button,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Stack,
    Typography
} from "@mui/material";
import FormSelect from "../../../experiments/forms/FormSelect";

interface Props {
    open: boolean;
    form?: any;
    title: string,
    body: string,
    leftButton: string,
    rightButton: string,
    viewNote?: boolean,
    leftFunction: () => void,
    rightFunction?: () => void;
    onClose: () => void;
}

const CreateReportModal = (props: Props) => {
    const { t } = useTranslation("finance", { keyPrefix: "payments.amex.transactions" });
    const {t: tg} = useTranslation("translation");
    const {  generateReport } = useAmexReportsRepository();
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);
    const {  globalData: {user}, dispatchGlobalData } = useContext(GlobalContext);
    const onClose = () => {
        props.onClose();
    };

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>([]);

    const schema = Yup.object().shape({
        legalEntity: Yup.string().required(t('modal.messages.requiredCheck')),
        dealer: Yup.string().required(t('modal.messages.requiredCheck')),
    });

    const defaultValues = () => {
        return {
            legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
            dealer: ""
        }
    };

    const {watch, control, reset} = useForm<any>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues()
    });

    const handleChangeLegalEntities = (value: any) => {
        console.log("handleChangeLegalEntities", value)
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }

    useEffect(() => {
        if (legalEntityOptions.length == 1 && dealers.length == 0) {
            setTimeout(() => {
                handleChangeLegalEntities(legalEntityOptions[0].value)
            });
        }
    })


    function createReport(){
        onClose();
        generateReport( watch("legalEntity"), watch("dealer") ).then((response) => {
            const count = response.result?.count;
            let message = count && count >= 0
            ? t("snackbar.success-create", { count: count })
            : (count === 0 ? t("snackbar.error-create-no-request") : t("snackbar.success-create"));
            setUpdating(true);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: message,
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    }
                }
            });  
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-create'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }


    return (
        <Dialog fullWidth={true} open={props.open} onClose={() => {
            return;
        }}>
            <DialogTitle>
                <Grid
                    container
                    alignItems="center"
                    alignContent="center"
                    direction="row"
                >
                    <Grid xs item>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <MuiIcons.Info sx={{color: '#C8017D'}} fontSize="small"/>
                            <Box>{props.title}</Box>
                        </Stack>
                    </Grid>
                    <Grid
                        xs
                        item
                        sx={{
                            justifyContent: "flex-end",
                            display: "flex",
                        }}
                    >
                        <MuiIcons.Close
                            color="disabled"
                            fontSize="small"
                            onClick={onClose}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <Grid>
                <DialogContent sx={{ padding: "6px 16px" }}>
                    <Grid container spacing={2} sx={{padding: "15px"}} alignItems={"center"}>
                        <Grid item xs={6}>
                            <FormSelect
                                name={"legalEntity"}
                                control={control}
                                props={{
                                    label: tg("shared.legalEntities"),
                                    fullWidth: true,
                                    required: true,
                                    onChange: (e: any) => {
                                        handleChangeLegalEntities(e.target.value)
                                    },
                                }}
                                items={legalEntityOptions.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormSelect
                                name={"dealer"}
                                control={control}
                                props={{
                                    label: tg("shared.dealers"),
                                    fullWidth: true,
                                    required: true,
                                    disabled: dealers.length == 0
                                }}
                                items={dealers.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            {props.body}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Stack direction="row" spacing={2} p={1} mr={1}>
                        <Button
                            sx={{
                                maxWidth: "111px",
                                maxHeight: "48px",
                                minWidth: "111px",
                                minHeight: "48px",
                            }}
                            variant="outlined"
                            onClick={props.leftFunction}
                        >
                            <Typography
                                sx={{
                                    textTransform: "capitalize",
                                    fontWeight: 700,
                                    lineHeight: 1.25,
                                    color: "#C8017D",
                                }}
                                variant="subtitle1"
                            >
                                {props.leftButton}
                            </Typography>
                        </Button>

                        <Button
                            sx={{
                                maxWidth: "181px",
                                maxHeight: "48px",
                                minWidth: "111px",
                                minHeight: "48px",
                            }}
                            disabled={watch("legalEntity") === "" || watch("dealer") === ""}
                            type={'button'}
                            variant="contained"
                            color="accent"
                            onClick={createReport}
                        >
                            <Typography
                                sx={{
                                    textTransform: "capitalize",
                                    fontWeight: 700,
                                    lineHeight: 1.25,
                                    color: 'white'
                                }}
                                variant="subtitle1"
                            >
                                {props.rightButton}
                            </Typography>
                        </Button>
                    </Stack>
                </DialogActions>
            </Grid>


        </Dialog>

    );

}

export default CreateReportModal;